import React, { useEffect, useState } from 'react'
import PageHeader from '../../PageHeader/PageHeader'
import { HitApi } from '../../Api/ApiHit'
import { searchUser, searchAccount, searchAccountTransaction, updateUser } from '../../constant/Constant'
import SearchableSelect from '../../components/AppSelect/SearchableSelect'
import { getSelectedUnit } from '../../storage/Storage'
import toast from 'react-hot-toast'
import AppButton from '../../components/AppButton/AppButton'
import { useDispatch, useSelector } from 'react-redux'
import AppTable from '../../components/AppTable/AppTable'
import { setApiJson } from '../../redux/Actions/ApiAction'
import { setPagination } from '../../redux/Actions/TableAction'

function AccountMaster() {
  const dispatch = useDispatch();
  const [staffOptions, setStaffOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);
  const [accountData, setAccountData] = useState(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState(false);
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const TableDataReducer = useSelector((state) => state.TableReducer);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (ApiReducer?.apiJson?.staffIds) {
      fetchCashTransactions();
      fetchAccounts();
    }
  }, [ApiReducer?.apiJson?.staffIds, TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

  const fetchCashTransactions = () => {
    setIsLoading(true);
    const json = {
      page: TableDataReducer.pagination.page,
      limit: TableDataReducer.pagination.limit,
      search: {
        userId: ApiReducer?.apiJson?.staffIds
      }
    };

    HitApi(json, searchAccountTransaction).then((res) => {
      console.log("Account transactions res=", res);
      setIsLoading(false);
      
      if (res?.statusCode === 200 && res?.data) {
        const transactions = res.data.docs || [];
        setTransactionsData(transactions);

        dispatch(setPagination({
          total: res.data.totalDocs || 0,
          page: res.data.page || 1,
          limit: res.data.limit || 10,
          totalPages: res.data.totalPages || 1,
          hasNextPage: res.data.hasNextPage || false,
          hasPrevPage: res.data.hasPrevPage || false
        }));
      } else {
        setTransactionsData([]);
        toast.error('No transactions found');
      }
    }).catch((error) => {
      console.error("Error fetching account transactions:", error);
      setIsLoading(false);
      setTransactionsData([]);
      toast.error('Failed to load account transactions');
    });
  };

  const fetchUsers = () => {
    const json = {
      page: 1,
      limit: 500,
      search: {
        unitIds: getSelectedUnit()?._id
      }
    };

    HitApi(json, searchUser).then((res) => {
      if (res?.statusCode === 200) {
        const options = res.data.docs?.map(staff => ({
          value: staff._id,
          label: staff.name,
        }));
        setStaffOptions(options);
      }
    }).catch((error) => {
      console.error("Error fetching staff:", error);
      toast.error('Failed to load staff options');
    });
  };

  const fetchAccounts = () => {
    if (!ApiReducer?.apiJson?.staffIds) {
      return;
    }

    setIsLoadingAccount(true);
    const json = {
      page: 1,
      limit: 1,
      search: {
        userId: ApiReducer?.apiJson?.staffIds
      }
    };

    HitApi(json, searchAccount).then((res) => {
      console.log("Account response:", res);
      setIsLoadingAccount(false);
      
      if (res?.statusCode === 200 && res?.data) {
        const accountInfo = res.data.docs?.[0] || res.data;
        if (res.data.docs?.length > 0) {
          setAccountData(accountInfo);
        } else {
          // No account found, create one by calling updateUser API
          createAccount();
        }
      } else {
        setAccountData(null);
        toast.error('No account data found');
      }
    }).catch((error) => {
      console.error("Error fetching account:", error);
      setIsLoadingAccount(false);
      setAccountData(null);
      toast.error('Failed to load account data');
    });
  };

  const createAccount = () => {
    if (!ApiReducer?.apiJson?.staffIds) {
      return;
    }

    setIsLoadingAccount(true);
    const json = {
      _id: ApiReducer.apiJson.staffIds
    };

    HitApi(json, updateUser).then((res) => {
      console.log("Create account response:", res);
      
      if (res?.statusCode === 200) {
        toast.success('Account created successfully');
        // Retry fetching the account after creation
        setTimeout(() => {
          fetchAccounts();
        }, 500);
      } else {
        setIsLoadingAccount(false);
        setAccountData(null);
        toast.error('Failed to create account');
      }
    }).catch((error) => {
      console.error("Error creating account:", error);
      setIsLoadingAccount(false);
      setAccountData(null);
      toast.error('Failed to create account');
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    const value = parseFloat(amount);
    if (value < 0) {
      return `₹-${Math.abs(value).toLocaleString('en-IN')}`;
    }
    return `₹${value.toLocaleString('en-IN')}`;
  };

  // Format date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${dateStr}, ${timeStr}`;
  };

  const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

  const tableHeaders = [
    {
      title: "S.No",
      key: "serialNumber",
      width: "60px",
      align: "center",
      priority: 1,
      render: (_, row, index) => (
        <div className="text-center">
          <span className="text-sm font-medium text-gray-900">
            {baseIndex + index + 1}
          </span>
        </div>
      )
    },
    {
      title: "Date & Time",
      key: "transactionDate",
      priority: 1,
      render: (value) => (
        <div className="text-sm text-gray-700">
          {formatDateTime(value)}
        </div>
      )
    },
    {
      title: "Type",
      key: "transactionType",
      align: "center",
      priority: 1,
      render: (value) => (
        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
          value === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value === 'credit' ? 'Credit' : 'Debit'}
        </span>
      )
    },
    {
      title: "Reference Type",
      key: "referenceType",
      priority: 2,
      render: (value) => (
        <span className="text-sm font-medium text-gray-700 capitalize">
          {value || 'N/A'}
        </span>
      )
    },
    {
      title: "Description",
      key: "description",
      priority: 1,
      render: (value) => (
        <div className="text-sm text-gray-600 max-w-xs truncate" title={value}>
          {value || 'N/A'}
        </div>
      )
    },
    {
      title: "Payment Method",
      key: "paymentMethod",
      priority: 3,
      render: (value) => (
        <span className="text-xs font-medium text-gray-600 capitalize">
          {value || 'N/A'}
        </span>
      )
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      priority: 1,
      render: (value, row) => (
        <div className={`text-sm font-bold ${
          row.transactionType === 'credit' ? 'text-green-600' : 'text-red-600'
        }`}>
          {row.transactionType === 'credit' ? '+' : '-'} {formatCurrency(value)}
        </div>
      )
    },
    {
      title: "Balance After",
      key: "balanceAfter",
      align: "right",
      priority: 1,
      render: (value) => (
        <div className="text-sm font-semibold text-gray-900">
          {formatCurrency(value)}
        </div>
      )
    }
  ];

  return (
    <div className='p-5'>
      <PageHeader
        title="Account Management"
        description="View, create, and manage financial accounts, transactions, and balances"
      />

      {/* Account Summary Section */}
      {accountData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Summary</h3>
          
          {isLoadingAccount ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Account Name</p>
                      <p className="text-lg font-semibold text-blue-900">
                        {accountData.accountName || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`rounded-lg p-4 ${accountData.balance < 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${accountData.balance < 0 ? 'text-red-600' : 'text-green-600'}`}>Current Balance</p>
                      <p className={`text-lg font-semibold ${accountData.balance < 0 ? 'text-red-900' : 'text-green-900'}`}>
                        {formatCurrency(accountData.balance)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-emerald-600">Total Cash In</p>
                      <p className="text-lg font-semibold text-emerald-900">
                        {formatCurrency(accountData.totalIn)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600">Total Cash Out</p>
                      <p className="text-lg font-semibold text-red-900">
                        {formatCurrency(accountData.totalOut)}
                      </p>
                    </div>
                  </div>
                </div>

                {accountData.accountType && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-purple-600">Account Type</p>
                        <p className="text-lg font-semibold text-purple-900 capitalize">
                          {accountData.accountType}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {accountData.isActive !== undefined && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Status</p>
                        <p className={`text-lg font-semibold ${accountData.isActive ? 'text-green-900' : 'text-red-900'}`}>
                          {accountData.isActive ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {(accountData.createdAt || accountData.updatedAt) && (
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-indigo-600">Last Updated</p>
                        <p className="text-sm font-medium text-indigo-900">
                          {accountData.updatedAt ? 
                            formatDateTime(accountData.updatedAt) : 
                            formatDateTime(accountData.createdAt)
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Disclaimer */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <span className="font-medium">Balance Explanation:</span> 
                        {accountData.balance < 0 ? (
                          <span className="text-red-700"> Negative balance means the salon owes money to the user.</span>
                        ) : (
                          <span className="text-green-700"> Positive balance means the user owes money to the salon.</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      
      <div className='flex justify-between items-center gap-4 mb-4'>
        <div className='w-full'>
          <SearchableSelect
            title="Select Staff"
            name="staffIds"
            options={staffOptions}
            placeholder="Select staff members"
            specification="Choose staff members to view their transactions"
            disabled={isLoading}
            size="md"
          />
        </div>
        <AppButton 
          buttontext="ManageTransactions" 
          className='mb-3'
          onClick={() => {/* Add your manage transactions logic */}}
        />
      </div>

      <AppTable
        TH={tableHeaders}
        TD={transactionsData}
        isLoading={isLoading}
      />
    </div>
  )
}

export default AccountMaster